import React, { useState, useMemo, useRef, useEffect } from 'react';
import { VitalHistory } from '../types';

interface VitalAnalyticsCardProps {
  data: VitalHistory[];
  rawData?: unknown;
}

export function countGlucoseReadings(rawData: unknown): number {
  if (!rawData || typeof rawData !== 'object') return 0;
  const root = rawData as Record<string, any>;
  const source = root.vitals_last_30_days && typeof root.vitals_last_30_days === 'object'
    ? root.vitals_last_30_days
    : root;
  const readings = Array.isArray(source.glucose) ? source.glucose : [];
  return readings.filter(
    (reading: any) =>
      reading &&
      typeof reading.recorded_at === 'string' &&
      reading.recorded_at &&
      Number.isFinite(Number(reading.glucose ?? reading.blood_glucose ?? reading.glucose_level ?? reading.value))
  ).length;
}

/**
 * Generate 30 days of zero-value placeholder records ending today.
 * This ensures the chart skeleton (axes, grid, labels) is always rendered
 * even when there is no actual vital data.
 */
function generateEmptyVitalHistory(): VitalHistory[] {
  const result: VitalHistory[] = [];
  const today = new Date();
  for (let i = 29; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    result.push({
      date: d.toISOString().split('T')[0],
      bp: { systolic: 0, diastolic: 0 },
      hr: 0,
      glucose: 0,
      weight: 0,
    });
  }
  return result;
}

type VitalType = 'BP' | 'HR' | 'Glucose' | 'Weight';

/**
 * Replace zeros in a numeric series so the trend chart doesn't drop to 0 on
 * days with no recorded vital. Rules:
 *
 *  - Zero(s) at the start    -> use the nearest non-zero value to the right.
 *      [0, 40, 41]            -> [40, 40, 41]
 *  - Zero(s) at the end      -> use the nearest non-zero value to the left.
 *      [60, 55, 79, 0]        -> [60, 55, 79, 79]
 *  - Zero(s) in the interior -> linearly interpolate between the nearest
 *    non-zero anchors on either side, producing a smooth slope rather than
 *    a flat midpoint plateau.
 *      [20, 0, 0, 0, 15]      -> [20, 18.75, 17.5, 16.25, 15]
 *      [40, 41, 60, 55, 0, 0, 79]
 *                             -> [40, 41, 60, 55, 63, 71, 79]
 *
 * If the entire series is zero (no data at all), it is returned unchanged so
 * the placeholder rendering keeps working.
 */
function interpolateZeros(values: number[]): number[] {
    const n = values.length;
    if (n === 0) return values;
    const result = values.slice();
    let i = 0;
    while (i < n) {
        if (values[i] !== 0) { i++; continue; }
        // Find the contiguous run of zeros [start, end] in the ORIGINAL array.
        const start = i;
        let end = i;
        while (end + 1 < n && values[end + 1] === 0) end++;
        const leftIdx = start - 1;
        const rightIdx = end + 1;
        const left = leftIdx >= 0 ? values[leftIdx] : null;
        const right = rightIdx < n ? values[rightIdx] : null;
        if (left !== null && right !== null) {
            // Linear interpolation across the gap. The step size accounts for
            // the gap WIDTH (end - start + 2) so the values walk smoothly
            // from `left` (at leftIdx) to `right` (at rightIdx).
            const span = rightIdx - leftIdx; // distance in slots between anchors
            const step = (right - left) / span;
            for (let k = start; k <= end; k++) {
                result[k] = left + step * (k - leftIdx);
            }
        } else if (left !== null) {
            for (let k = start; k <= end; k++) result[k] = left;
        } else if (right !== null) {
            for (let k = start; k <= end; k++) result[k] = right;
        }
        // else: entire series is zero — leave as 0
        i = end + 1;
    }
    return result;
}

const BPIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>;
const HRIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>;
const GlucoseIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4H7z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 21a4 4 0 004-4V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4z" /></svg>;
const WeightIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l-3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l-3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3m-3 0l-3-4.5M12 5l3-4.5M12 5l-3 4.5" /></svg>;

const VitalTrendGraph = ({ data, vitalType, unit, color = '#5A46AA', hasRealData = true }: { data: VitalHistory[]; vitalType: VitalType; unit: string; color?: string; hasRealData?: boolean; }) => {
    const [activePoint, setActivePoint] = useState<{ index: number; record: VitalHistory } | null>(null);
    const svgRef = useRef<SVGSVGElement>(null);

    const systolicColor = color;
    const diastolicColor = '#A2ADD6';

    const chartMetrics = useMemo(() => {
        const SVG_WIDTH = 400;
        const SVG_HEIGHT = 150;
        const margin = { top: 20, right: 25, bottom: 25, left: 25 };
        const width = SVG_WIDTH - margin.left - margin.right;
        const height = SVG_HEIGHT - margin.top - margin.bottom;

        if (!data || data.length < 2) {
            return { points: [], SVG_WIDTH, SVG_HEIGHT, margin, width, height, yAxisLabels: [], startDate: '', endDate: '' };
        }
        
        const controlPoint = (current: any, previous: any, next: any, end?: boolean) => {
            const p = previous || current;
            const n = next || current;
            const smoothing = 0.2;
            const o = { x: n.x - p.x, y: n.y - p.y };
            const angle = Math.atan2(o.y, o.x);
            const length = Math.sqrt(o.x * o.x + o.y * o.y) * smoothing;
            const x = current.x + Math.cos(angle) * (end ? -1 : 1) * length;
            const y = current.y + Math.sin(angle) * (end ? -1 : 1) * length;
            return [x, y];
        }
        const lineCommand = (point: { x: number, y: number }, i: number, a: { x: number, y: number }[]) => {
            const [cpsX, cpsY] = controlPoint(a[i-1], a[i-2], point);
            const [cpeX, cpeY] = controlPoint(point, a[i-1], a[i+1], true);
            return `C ${cpsX},${cpsY} ${cpeX},${cpeY} ${point.x},${point.y}`;
        }
        const formatDate = (date: string) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        
        const startDate = formatDate(data[0].date);
        const endDate = formatDate(data[data.length-1].date);
        const xScale = (index: number) => margin.left + (index / (data.length - 1)) * width;

        if (vitalType === 'BP') {
            const systolicValues = interpolateZeros(data.map(d => d.bp.systolic));
            const diastolicValues = interpolateZeros(data.map(d => d.bp.diastolic));
            const dataMin = Math.min(...diastolicValues);
            const dataMax = Math.max(...systolicValues);
            const yPadding = (dataMax - dataMin) * 0.15 || 5;
            const yMin = Math.max(0, dataMin - yPadding);
            const yMax = dataMax + yPadding;
            
            const yScale = (value: number) => margin.top + height - ((value - yMin) / (yMax - yMin)) * height;

            const systolicPoints = data.map((d, i) => ({ x: xScale(i), y: yScale(systolicValues[i]), record: d }));
            const diastolicPoints = data.map((d, i) => ({ x: xScale(i), y: yScale(diastolicValues[i]), record: d }));
            
            const createPath = (points: any[]) => points.reduce((acc, p, i, a) => i === 0 ? `M ${p.x},${p.y}` : `${acc} ${lineCommand(p, i, a)}`, '');
            const systolicLinePath = createPath(systolicPoints);
            const diastolicLinePath = createPath(diastolicPoints);
            
            const createAreaPath = (linePath: string, points: any[]) => `${linePath} L ${points[points.length-1].x},${margin.top + height} L ${points[0].x},${margin.top + height} Z`;
            const systolicAreaPath = createAreaPath(systolicLinePath, systolicPoints);
            const diastolicAreaPath = createAreaPath(diastolicLinePath, diastolicPoints);
            
            const yAxisLabels = Array.from(new Set([Math.round(yMin), Math.round(dataMin), Math.round(dataMax), Math.round(yMax)]))
                                     .map(value => ({ value, y: yScale(value) }));

            return { SVG_WIDTH, SVG_HEIGHT, margin, width, height, points: systolicPoints, systolicPoints, diastolicPoints, systolicLinePath, diastolicLinePath, systolicAreaPath, diastolicAreaPath, yAxisLabels, startDate, endDate };

        } else {
            const rawValues = data.map(d => d[vitalsConfig[vitalType].dataKey as keyof Omit<VitalHistory, 'date' | 'bp'>] as number);
            const values = interpolateZeros(rawValues);
            const dataMin = Math.min(...values);
            const dataMax = Math.max(...values);
            const yPadding = (dataMax - dataMin) * 0.1 || 5;
            const yMin = Math.max(0, dataMin - yPadding);
            const yMax = dataMax + yPadding;
            const yScale = (value: number) => margin.top + height - ((value - yMin) / (yMax - yMin)) * height;
            
            const points = data.map((d, i) => ({ x: xScale(i), y: yScale(values[i]), record: d }));
            const linePath = points.reduce((acc, p, i, a) => i === 0 ? `M ${p.x},${p.y}` : `${acc} ${lineCommand(p, i, a)}`, '');
            const areaPath = `${linePath} L ${points[points.length-1].x},${margin.top + height} L ${points[0].x},${margin.top + height} Z`;
            const yAxisLabels = [{ value: Math.round(yMin), y: margin.top + height }, { value: Math.round(dataMax), y: yScale(dataMax) }, { value: Math.round(yMax), y: margin.top }];
            
            return { SVG_WIDTH, SVG_HEIGHT, margin, width, height, points, linePath, areaPath, yAxisLabels, startDate, endDate };
        }
    }, [data, vitalType]);

    const { SVG_WIDTH = 400, SVG_HEIGHT = 150, margin, width, points, linePath, areaPath, systolicPoints, diastolicPoints, systolicLinePath, diastolicLinePath, systolicAreaPath, diastolicAreaPath, yAxisLabels, startDate, endDate } = chartMetrics;

    const handleMouseMove = (event: React.MouseEvent<SVGSVGElement>) => {
        if (!hasRealData) return; // No tooltips when displaying placeholder data
        if (!svgRef.current || !points || points.length === 0) return;
        const svgRect = svgRef.current.getBoundingClientRect();
        const x = event.clientX - svgRect.left - margin.left;
        const index = Math.max(0, Math.min(points.length - 1, Math.round((x / width) * (points.length - 1))));
        setActivePoint({ index, record: points[index].record });
    };
    const handleMouseLeave = () => setActivePoint(null);

    if (points.length === 0) {
        return <div className="w-full h-48 bg-gray-50 rounded-lg flex items-center justify-center text-gray-400">Not enough data to display chart.</div>
    }

    const activeDataPoint = activePoint ? points[activePoint.index] : null;
    const activeSystolicPoint = activePoint && systolicPoints ? systolicPoints[activePoint.index] : null;
    const activeDiastolicPoint = activePoint && diastolicPoints ? diastolicPoints[activePoint.index] : null;

    return (
        <div className="relative">
            {vitalType === 'BP' && (
                <div className="flex justify-center items-center gap-4 text-xs text-gray-600 mb-1">
                    <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full" style={{ backgroundColor: systolicColor }}></span>Systolic</div>
                    <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full" style={{ backgroundColor: diastolicColor }}></span>Diastolic</div>
                </div>
            )}
            <svg ref={svgRef} viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`} className="w-full h-auto" onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
                <defs>
                    <linearGradient id="systolic-gradient" x1="0" x2="0" y1="0" y2="1">
                        <stop offset="0%" stopColor={systolicColor} stopOpacity="0.2" />
                        <stop offset="100%" stopColor={systolicColor} stopOpacity="0" />
                    </linearGradient>
                    <linearGradient id="diastolic-gradient" x1="0" x2="0" y1="0" y2="1">
                        <stop offset="0%" stopColor={diastolicColor} stopOpacity="0.2" />
                        <stop offset="100%" stopColor={diastolicColor} stopOpacity="0" />
                    </linearGradient>
                </defs>

                {yAxisLabels.map(({ value, y }) => (
                    <g key={y}>
                        <line x1={margin.left} y1={y} x2={margin.left + width} y2={y} stroke="#E8E9F1" strokeWidth="1" strokeDasharray="3,3" />
                        <text x={margin.left - 8} y={y + 4} textAnchor="end" fill="#9CA3AF" fontSize="10">{value}</text>
                    </g>
                ))}
                <text x={margin.left} y={SVG_HEIGHT - 8} textAnchor="start" fill="#9CA3AF" fontSize="10">{startDate}</text>
                <text x={SVG_WIDTH - margin.right} y={SVG_HEIGHT - 8} textAnchor="end" fill="#9CA3AF" fontSize="10">{endDate}</text>

                {vitalType === 'BP' ? (
                    <>
                        <path d={systolicAreaPath} fill="url(#systolic-gradient)" />
                        <path d={diastolicAreaPath} fill="url(#diastolic-gradient)" />
                        <path d={diastolicLinePath} fill="none" stroke={diastolicColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d={systolicLinePath} fill="none" stroke={systolicColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                    </>
                ) : (
                    <>
                        <path d={areaPath} fill="url(#systolic-gradient)" />
                        <path d={linePath} fill="none" stroke={systolicColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                    </>
                )}
                
                {activePoint && (
                    <g className="transition-opacity duration-200" style={{ pointerEvents: 'none' }}>
                        <line x1={activeDataPoint?.x ?? 0} y1={margin.top} x2={activeDataPoint?.x ?? 0} y2={margin.top + chartMetrics.height} stroke={color} strokeWidth="1" strokeDasharray="3,3" opacity={0.5} />
                        {vitalType === 'BP' && activeSystolicPoint && activeDiastolicPoint ? (
                            <>
                                <circle cx={activeSystolicPoint.x} cy={activeSystolicPoint.y} r="5" fill="white" stroke={systolicColor} strokeWidth="2" />
                                <circle cx={activeDiastolicPoint.x} cy={activeDiastolicPoint.y} r="5" fill="white" stroke={diastolicColor} strokeWidth="2" />
                            </>
                        ) : (
                             <circle cx={activeDataPoint?.x ?? 0} cy={activeDataPoint?.y ?? 0} r="5" fill="white" stroke={systolicColor} strokeWidth="2" />
                        )}
                    </g>
                )}
            </svg>
            {activePoint && (
                <div 
                    className="absolute bg-white rounded-lg shadow-lg p-2 text-xs pointer-events-none transition-transform duration-100"
                    style={{
                        left: `${(activeDataPoint?.x ?? 0 / SVG_WIDTH) * 100}%`,
                        top: `${((vitalType === 'BP' ? activeSystolicPoint?.y ?? 0 : activeDataPoint?.y ?? 0) / SVG_HEIGHT) * 100}%`,
                        transform: `translate(-50%, -120%)`,
                        minWidth: '100px',
                    }}
                >
                    <p className="font-bold text-gray-800 text-center">
                        {vitalType === 'BP' 
                            ? `${activePoint.record.bp.systolic}/${activePoint.record.bp.diastolic}`
                            : activePoint.record[vitalsConfig[vitalType].dataKey as keyof Omit<VitalHistory, 'date'|'bp'>]
                        }
                        <span className="font-normal text-gray-500 ml-1">{unit}</span>
                    </p>
                    <p className="text-gray-500 text-center">{new Date(activePoint.record.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</p>
                </div>
            )}
        </div>
    );
};

const vitalsConfig: Record<VitalType, { label: string; unit: string; icon: React.ReactNode; dataKey: keyof VitalHistory | 'bp' }> = {
    BP: { label: 'Blood Pressure', unit: 'mmHg', icon: <BPIcon />, dataKey: 'bp' },
    HR: { label: 'Heart Rate', unit: 'bpm', icon: <HRIcon />, dataKey: 'hr' },
    Glucose: { label: 'Glucose', unit: 'mg/dL', icon: <GlucoseIcon />, dataKey: 'glucose' },
    Weight: { label: 'Weight', unit: 'kg', icon: <WeightIcon />, dataKey: 'weight' },
};

const VitalAnalyticsCard: React.FC<VitalAnalyticsCardProps> = ({ data, rawData }) => {
  const [activeVital, setActiveVital] = useState<VitalType>('BP');
  const [isGraphOpen, setIsGraphOpen] = useState(true);
  const availableVitals = useMemo<VitalType[]>(
    () => countGlucoseReadings(rawData) >= 2 ? ['BP', 'HR', 'Glucose', 'Weight'] : ['BP', 'HR', 'Weight'],
    [rawData]
  );
  useEffect(() => {
    if (!availableVitals.includes(activeVital)) setActiveVital(availableVitals[0]);
  }, [activeVital, availableVitals]);

  // Determine if there is real data (at least one record in the past 30 days)
  const hasRealData = data && data.length > 0;

  // Per-vital "no data" detection. Even when the overall response has 30
  // days of records, a specific vital (e.g. glucose with an empty array)
  // may have all-zero values. In that case we want to display "--" instead
  // of a flat-zero line. Weight with a single record still has at least
  // one non-zero entry, so this returns false for weight and the chart can
  // render a flat line via interpolateZeros.
  const isActiveVitalEmpty = useMemo(() => {
    if (!hasRealData) return true;
    if (activeVital === 'BP') {
      return data.every(d => d.bp.systolic === 0 && d.bp.diastolic === 0);
    }
    const key = vitalsConfig[activeVital].dataKey as keyof Omit<VitalHistory, 'date' | 'bp'>;
    return data.every(d => (d[key] as number) === 0);
  }, [activeVital, data, hasRealData]);

  // Use placeholder zero-filled data for the graph when there's no real data
  const graphData = useMemo(() => {
    return hasRealData ? [...data].sort((a, b) => a.date.localeCompare(b.date)) : generateEmptyVitalHistory();
  }, [data, hasRealData]);

  const analysis = useMemo(() => {
    if (isActiveVitalEmpty) {
      // No data for this specific vital: show placeholder values
      const placeholders: Record<VitalType, string> = {
        BP: '--/--',
        HR: '--',
        Glucose: '--',
        Weight: '--',
      };
      return { latestValue: placeholders[activeVital], change: 0, trend: 'stable' as const, isAbnormal: false, noData: true };
    }

    const { dataKey } = vitalsConfig[activeVital];

    // Helper: find the latest record where the active vital has a non-zero
    // value, scanning from newest to oldest. Some days carry forward via
    // `lastValid` in `transformVitalHistory`, but if a later day reports
    // BP/HR without a weight reading, the propagated weight resets to 0.
    // Reading the latest non-zero value avoids showing a stale 0 in the KPI.
    const isNonZero = (record: VitalHistory) =>
        dataKey === 'bp'
            ? record.bp.systolic !== 0 || record.bp.diastolic !== 0
            : (record[dataKey as keyof Omit<VitalHistory, 'date' | 'bp'>] as number) !== 0;
    const latestNonZeroIdx = (() => {
        for (let i = data.length - 1; i >= 0; i--) if (isNonZero(data[i])) return i;
        return data.length - 1;
    })();
    const latestRecord = data[latestNonZeroIdx];
    // For the 7-day-ago comparison, walk back 7 days from the latest
    // non-zero record and again skip zeros so the trend doesn't compare
    // against a missing day.
    const prevTargetIdx = Math.max(0, latestNonZeroIdx - 7);
    const prevRecord = (() => {
        for (let i = prevTargetIdx; i >= 0; i--) if (isNonZero(data[i])) return data[i];
        return data[0];
    })();

    let latestValueNum: number;
    let prevValueNum: number;
    let formattedValue: string;
    let isAbnormal = false;

    if (dataKey === 'bp') {
      const { systolic, diastolic } = latestRecord.bp;
      latestValueNum = (systolic + diastolic) / 2; // Use average for trend calculation
      prevValueNum = (prevRecord.bp.systolic + prevRecord.bp.diastolic) / 2;
      formattedValue = `${systolic}/${diastolic}`;
      if (systolic > 140 || diastolic > 90 || systolic < 90 || diastolic < 60) {
        isAbnormal = true;
      }
    } else {
      const key = dataKey as keyof Omit<VitalHistory, 'date' | 'bp'>;
      latestValueNum = latestRecord[key] as number;
      prevValueNum = prevRecord[key] as number;
      formattedValue = latestValueNum.toString();
      
      switch(activeVital) {
        case 'HR':
          if (latestValueNum < 60 || latestValueNum > 100) {
            isAbnormal = true;
          }
          break;
        case 'Glucose':
          if (latestValueNum < 70 || latestValueNum > 180) {
            isAbnormal = true;
          }
          break;
        case 'Weight':
        default:
          isAbnormal = false;
          break;
      }
    }
    
    const change = prevValueNum !== 0 ? ((latestValueNum - prevValueNum) / prevValueNum) * 100 : 0;
    const trend = change > 5 ? 'up' : change < -5 ? 'down' : 'stable';

    return { latestValue: formattedValue, change, trend, isAbnormal, noData: false };
  }, [activeVital, data, hasRealData, isActiveVitalEmpty]);

  const { latestValue, change, trend, isAbnormal, noData } = analysis as { latestValue: string; change: number; trend: string; isAbnormal: boolean; noData: boolean };

  return (
    <div className="p-6 bg-white rounded-2xl shadow-sm flex flex-col">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-bold text-gray-800">Vital Analytics</h3>
        <span className="text-sm text-gray-500">Past 30 Days</span>
      </div>
      
      <div className="grid grid-cols-4 gap-2 mb-4">
        {availableVitals.map(vital => {
          const isActive = activeVital === vital;
          return (
            <button
              key={vital}
              onClick={() => setActiveVital(vital)}
              className={`flex flex-col items-center justify-center p-2 rounded-lg text-center transition-all duration-200 border-2 ${
                isActive ? 'bg-primary-light border-primary text-primary' : 'bg-gray-50 border-transparent hover:bg-light-gray'
              }`}
              aria-pressed={isActive}
            >
              <div className={isActive ? 'text-primary' : 'text-gray-600'}>{vitalsConfig[vital].icon}</div>
              <span className="text-xs font-semibold mt-1">{vitalsConfig[vital].label}</span>
            </button>
          )
        })}
      </div>

      <div className="text-center my-4">
        <div className="flex items-baseline justify-center gap-2">
            <p className={`text-4xl font-bold ${noData ? 'text-gray-400' : (isAbnormal ? 'text-red-600' : 'text-accent-green')}`}>
                {latestValue}
            </p>
            {!noData && isAbnormal && (
                <span className="text-accent-yellow" title="Abnormal Reading">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 3.001-1.742 3.001H4.42c-1.53 0-2.493-1.667-1.743-3.001l5.58-9.92zM10 13a1 1 0 110-2 1 1 0 010 2zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                </span>
            )}
        </div>
        <p className="text-sm text-gray-500 font-medium">{vitalsConfig[activeVital].unit}</p>
        {!noData && (
          <div className="flex items-center justify-center gap-1 mt-1">
            {trend !== 'stable' && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`h-4 w-4 ${trend === 'up' ? 'text-red-500' : 'text-green-500'}`}
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                {trend === 'up' ? 
                  <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" /> :
                  <path fillRule="evenodd" d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" />
                }
              </svg>
            )}
            <span className={`text-sm font-bold ${trend === 'stable' ? 'text-gray-500' : (trend === 'up' ? 'text-red-500' : 'text-green-500')}`}>
              {trend === 'stable' ? 'Stable' : `${Math.abs(change).toFixed(0)}% ${trend}`}
            </span>
             <span className="text-sm text-gray-500">vs last 7 days</span>
          </div>
        )}
        {noData && (
          <p className="text-sm text-gray-400 mt-1">No data recorded</p>
        )}
      </div>

      <div className={`transition-all duration-500 ease-in-out overflow-hidden ${isGraphOpen ? 'max-h-[300px] opacity-100 mt-4' : 'max-h-0 opacity-0'}`}>
        <VitalTrendGraph 
            data={graphData} 
            vitalType={activeVital} 
            unit={vitalsConfig[activeVital].unit}
            color={noData ? '#D1D5DB' : (isAbnormal ? '#DC2626' : '#5A46AA')}
            hasRealData={!isActiveVitalEmpty}
        />
      </div>

      <div className="mt-auto pt-4 border-t border-light-gray">
        <button 
            onClick={() => setIsGraphOpen(prev => !prev)}
            className="w-full flex items-center justify-center gap-2 text-sm font-semibold text-primary hover:text-purple-700 transition-colors"
            aria-expanded={isGraphOpen}
        >
            <span>{isGraphOpen ? 'Hide Trend Graph' : 'Show Trend Graph'}</span>
             <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`h-5 w-5 transition-transform duration-300 ${isGraphOpen ? 'rotate-180' : ''}`}
                viewBox="0 0 20 20"
                fill="currentColor"
            >
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
        </button>
      </div>
    </div>
  );
};

export default VitalAnalyticsCard;
